/**
 * Sawyer 15 workweek-hours-ot (E2) — "This week's hours, before the week is
 * over." Per active staffer for a Sunday-to-Saturday workweek: Worked (punches,
 * an on-the-clock punch counted up to now), Still scheduled (published shifts
 * ahead, minus scheduled lunch), Projected, and Scheduled for the whole week.
 * Sawyer: overtime should be visible "the 4th day or 5th day", not after
 * payroll.
 *
 * Design Philosophy:
 *  - P1 one glance, one truth: the four figures per person in one row; the
 *    banner above reads the SAME response (shared hook in Timesheets).
 *  - P5 never lie about data: a week with no published shifts says the
 *    projection is worked-only; a stale open punch is excluded and flagged,
 *    with the way to fix it; the "as of" time is shown.
 *  - P6 tokens only (ferro-ts-chip families); P7 stacked cards at 375,
 *    ≥44 px targets, no hover-only affordance.
 * Five states: loading (skeleton rows) · empty (no staff / no shifts) ·
 * error (what happened + Try again) · default · focus (existing rings).
 */
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { addIsoDays } from '../../lib/api.js';
import { formatDayShort } from '../../lib/schedule-dates.js';
import { otBasisLabel } from '../../lib/timesheet-format.js';
import {
  hoursOneDecimal,
  savePayrollSettings,
  thresholdHoursLabel,
  type WorkweekHoursView,
  type WorkweekStaffRow,
} from '../../lib/workweek-api.js';
import type { WorkweekState } from './useWorkweekHours.js';
import './workweek-hours.css';

export const PANEL_ID = 'this-week';

const dayLabel = (iso: string) => formatDayShort(iso).replace(',', '');

function asOfLabel(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

export interface WorkweekHoursPanelProps {
  apiUrl: string;
  state: WorkweekState;
  onRetry: () => void;
  /** null = "this week" (server's tenant clock); a date picks its workweek. */
  onPickWeek: (date: string | null) => void;
  /** Owner (payroll.rates:write:all) may edit the alert threshold. */
  canEditThreshold: boolean;
  /** Route gate (rolesCanAccessPath): links render only for pages the viewer
   *  can open; everyone else gets the same words without a dead link. */
  canOpen: (path: string) => boolean;
  /** After a threshold save the workweek is re-read. */
  onThresholdSaved: () => void;
  /** Fired once after the first successful load (deep-link scroll). */
  onFirstReady?: () => void;
}

export function WorkweekHoursPanel(props: WorkweekHoursPanelProps): JSX.Element {
  const { state, onRetry, onPickWeek } = props;
  const firedReady = useRef(false);
  useEffect(() => {
    if (state.kind === 'ready' && !firedReady.current) {
      firedReady.current = true;
      props.onFirstReady?.();
    }
  }, [state, props]);

  const data = state.kind === 'ready' ? state.data : null;

  return (
    <section
      id={PANEL_ID}
      className="ferro-ts-week"
      aria-labelledby="ferro-ts-week-title"
      data-testid="workweek-panel"
    >
      <header className="ferro-ts-week__head">
        <div className="ferro-ts-week__titles">
          <h2 id="ferro-ts-week-title" className="ferro-ts-week__title">
            Hours this workweek
          </h2>
          {data ? (
            <p className="ferro-ts-week__range ferro-ts-tnum" data-testid="workweek-range">
              Week of {dayLabel(data.week_start)} - {dayLabel(data.week_end)}
            </p>
          ) : null}
        </div>
        <div className="ferro-ts-week__nav" role="group" aria-label="Workweek">
          <button
            type="button"
            className="ferro-btn ferro-btn--sm ferro-btn--outline ferro-ts-week__navbtn"
            onClick={() => data && onPickWeek(addIsoDays(data.week_start, -7))}
            disabled={!data}
            aria-label="Previous week"
            data-testid="workweek-prev"
          >
            ←
          </button>
          <button
            type="button"
            className="ferro-btn ferro-btn--sm ferro-btn--ghost ferro-ts-week__navbtn"
            onClick={() => onPickWeek(null)}
            data-testid="workweek-this-week"
          >
            This week
          </button>
          <button
            type="button"
            className="ferro-btn ferro-btn--sm ferro-btn--outline ferro-ts-week__navbtn"
            onClick={() => data && onPickWeek(addIsoDays(data.week_start, 7))}
            disabled={!data}
            aria-label="Next week"
            data-testid="workweek-next"
          >
            →
          </button>
        </div>
      </header>

      {data ? (
        <ThresholdLine
          apiUrl={props.apiUrl}
          data={data}
          canEdit={props.canEditThreshold}
          onSaved={props.onThresholdSaved}
        />
      ) : null}

      {state.kind === 'loading' ? (
        <div className="ferro-ts-week__skeleton" data-testid="workweek-skeleton" aria-busy="true">
          {[1, 2, 3].map((i) => (
            <div key={i} className="ferro-skeleton ferro-skeleton--table-row" />
          ))}
        </div>
      ) : state.kind === 'error' ? (
        <div className="ferro-alert ferro-alert--error" role="alert" data-testid="workweek-error">
          <span>This week&apos;s hours didn&apos;t load.</span>
          <button type="button" className="ferro-btn ferro-btn--sm ferro-btn--outline" onClick={onRetry}>
            Try again
          </button>
        </div>
      ) : data && data.staff.length === 0 ? (
        <div className="ferro-ts-empty" data-testid="workweek-empty-staff">
          <p className="ferro-ts-empty__lead">No staff to show this week.</p>
          {props.canOpen('/staff') ? (
            <Link className="ferro-btn ferro-btn--outline" to="/staff">
              Go to Staff
            </Link>
          ) : null}
        </div>
      ) : data ? (
        <>
          <table className="ferro-ts-week__table" data-testid="workweek-table">
            <thead>
              <tr>
                <th scope="col">Staff</th>
                <th scope="col">Worked</th>
                <th scope="col">Still scheduled</th>
                <th scope="col">Projected</th>
                <th scope="col">Scheduled (week)</th>
              </tr>
            </thead>
            <tbody>
              {data.staff.map((row) => (
                <StaffRow
                  key={row.staff_user_id}
                  row={row}
                  data={data}
                  canOpenPunchRecords={props.canOpen('/timeclock/manage')}
                />
              ))}
            </tbody>
          </table>
          {!data.has_published_shifts ? (
            <p className="ferro-ts-week__noshifts" data-testid="workweek-no-shifts">
              No published shifts this week, so projected hours are worked hours only.{' '}
              {props.canOpen('/schedule') ? (
                <Link to="/schedule">Publish the week on Schedule</Link>
              ) : (
                'Publish the week on Schedule'
              )}{' '}
              to see what&apos;s coming.
            </p>
          ) : null}
          <p className="ferro-ts-caption" data-testid="workweek-caption">
            Projected = worked so far + published shifts still ahead, minus scheduled lunch.
            {data.as_of ? ` As of ${asOfLabel(data.as_of)}.` : ''}{' '}
            <button type="button" className="ferro-ts-week__refresh" onClick={onRetry}>
              Refresh
            </button>
          </p>
        </>
      ) : null}
    </section>
  );
}

function StaffRow({
  row,
  data,
  canOpenPunchRecords,
}: {
  row: WorkweekStaffRow;
  data: WorkweekHoursView;
  canOpenPunchRecords: boolean;
}): JSX.Element {
  const stale = row.flags.includes('open_entry_stale');
  const nearThreshold =
    row.projected_overtime_minutes === 0 && row.projected_minutes >= data.threshold_minutes;
  return (
    <tr className="ferro-ts-week__row" data-testid={`workweek-row-${row.staff_user_id}`}>
      <th scope="row" className="ferro-ts-week__name">
        <span>{row.employee_name}</span>
        <span className="ferro-ts-week__chips">
          {row.on_the_clock ? <span className="ferro-ts-chip ferro-ts-chip--info">On the clock</span> : null}
          {stale ? (
            canOpenPunchRecords ? (
              <Link className="ferro-ts-chip ferro-ts-chip--danger ferro-ts-week__chiplink" to="/timeclock/manage" data-testid="workweek-stale-chip">
                Open punch, fix in Punch records
              </Link>
            ) : (
              <span className="ferro-ts-chip ferro-ts-chip--danger" data-testid="workweek-stale-chip">
                Open punch, fix in Punch records
              </span>
            )
          ) : null}
        </span>
      </th>
      <td data-label="Worked" className="ferro-ts-tnum" data-testid="workweek-worked">
        {hoursOneDecimal(row.worked_minutes)} h
      </td>
      <td data-label="Still scheduled" className="ferro-ts-tnum" data-testid="workweek-remaining">
        {hoursOneDecimal(row.remaining_scheduled_minutes)} h
      </td>
      <td data-label="Projected" className="ferro-ts-tnum" data-testid="workweek-projected">
        <span>{hoursOneDecimal(row.projected_minutes)} h</span>
        {row.projected_overtime_minutes > 0 ? (
          <>
            <span className="ferro-ts-chip ferro-ts-chip--danger" data-testid="workweek-ot-chip">
              {hoursOneDecimal(row.projected_overtime_minutes)} h OT
            </span>
            <span className="ferro-ts-chip ferro-ts-chip--info" data-testid="workweek-ot-basis">
              {otBasisLabel(row.projected_ot_basis)}
            </span>
          </>
        ) : nearThreshold ? (
          <span className="ferro-ts-chip ferro-ts-chip--warn" data-testid="workweek-near-chip">
            {hoursOneDecimal(Math.max(0, data.weekly_ot_minutes - row.projected_minutes))} h to OT
          </span>
        ) : null}
      </td>
      <td data-label="Scheduled (week)" className="ferro-ts-tnum ferro-ts-week__sched">
        {hoursOneDecimal(row.scheduled_minutes)} h
      </td>
    </tr>
  );
}

function ThresholdLine({
  apiUrl,
  data,
  canEdit,
  onSaved,
}: {
  apiUrl: string;
  data: WorkweekHoursView;
  canEdit: boolean;
  onSaved: () => void;
}): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const weekly = thresholdHoursLabel(data.weekly_ot_minutes);
  const current = thresholdHoursLabel(data.threshold_minutes);

  const start = () => {
    setValue(current);
    setError(null);
    setEditing(true);
  };

  const save = async () => {
    const hours = Number(value);
    const minutes = Math.round(hours * 60);
    if (value.trim() === '' || !Number.isFinite(hours) || hours < 1 || hours > 40 || minutes % 30 !== 0) {
      setError('Pick a threshold between 1 and 40 hours, in half-hour steps.');
      return;
    }
    setSaving(true);
    setError(null);
    const res = await savePayrollSettings(apiUrl, minutes);
    setSaving(false);
    if (!res.ok) {
      setError(res.message);
      return;
    }
    setEditing(false);
    onSaved();
  };

  if (editing) {
    return (
      <form
        // Our own inline message (AC13), not the browser's validation bubble.
        noValidate
        className="ferro-ts-week__threshold ferro-ts-week__threshold--edit"
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
        data-testid="threshold-form"
      >
        <label className="ferro-field ferro-ts-week__thresholdfield">
          <span className="ferro-field__label">Alert me at (hours of {weekly})</span>
          <input
            type="number"
            className="ferro-field__input"
            min={1}
            max={40}
            step={0.5}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            aria-invalid={error !== null}
            aria-describedby={error ? 'threshold-error' : undefined}
            data-testid="threshold-input"
          />
        </label>
        <div className="ferro-ts-week__thresholdactions">
          <button
            type="submit"
            className="ferro-btn ferro-btn--sm ferro-btn--primary"
            disabled={saving}
            aria-busy={saving}
            data-testid="threshold-save"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button
            type="button"
            className="ferro-btn ferro-btn--sm ferro-btn--ghost"
            onClick={() => setEditing(false)}
            disabled={saving}
            data-testid="threshold-cancel"
          >
            Cancel
          </button>
        </div>
        {error ? (
          <p id="threshold-error" className="ferro-ts-week__thresholderror" role="alert" data-testid="threshold-error">
            {error}
          </p>
        ) : null}
        <p className="ferro-ts-week__thresholdnow" data-testid="threshold-line">
          Alert at {current} h (of {weekly}){data.threshold_source === 'default' ? ', default' : ''}
        </p>
      </form>
    );
  }

  return (
    <div className="ferro-ts-week__threshold">
      <p className="ferro-ts-week__thresholdnow" data-testid="threshold-line">
        Alert at {current} h (of {weekly}){data.threshold_source === 'default' ? ', default' : ''}
        {canEdit ? null : <span className="ferro-muted">. Only an owner can change this.</span>}
      </p>
      {canEdit ? (
        <button
          type="button"
          className="ferro-btn ferro-btn--sm ferro-btn--outline"
          onClick={start}
          data-testid="threshold-edit"
        >
          Edit
        </button>
      ) : null}
    </div>
  );
}
